import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import { useSKV } from '~Hooks/Integrations/useSKV';
import * as actions from '~actions';
import OrderSyncer from '../OrderSyncer';
import { useStorage } from '~Components/Storage';

const MAX_FAIL_COUNT = 3;
interface Props {
  orderId: string;
  isStorageOrder?: boolean;
}

/**
 * Tries to sync order with Skatteverket
 *
 * If sync is successful we get a complianceId that gets passed to the regular OrderSyncer.
 * On fail, we retry x times. If still no luck we go ahead without complianceId.
 */
const SKV: React.FunctionComponent<Props> = ({ orderId, isStorageOrder }) => {
  const syncSKV = useSKV();
  const logger = useLogger('SKV');
  const dispatch = useDispatch();
  const [storage, updateStorage] = useStorage();
  const storageRef = useRef(storage);
  useEffect(() => {
    storageRef.current = storage;
  }, [storage]);
  const receiptCounter = useSelector((state) => state.POSReceiptCounter);
  const [proceedWithoutComplianceId, setProceedWithoutComplianceId] = useState(false);
  const { i18n } = useI18n();

  const orderFromRedux = useSelector((state) =>
    state.POSOrdersForSync.find((o) => o.order.id === orderId),
  );
  const orderFromStorage = storage.storedOrders.find((o) => o.order.id === orderId);

  const orderData = isStorageOrder ? orderFromStorage : orderFromRedux;

  const { order, amount } = orderData!;

  // @ts-ignore Joar: complianceId is not always set - ignore that
  const [complianceId, setComplianceId] = useState(orderData.complianceId);

  const creationDate = new Date();

  let failCount = 0;
  const run = async () => {
    const {
      success,
      errorMessage,
      controlUnitSerial,
      productionNumber,
      complianceId: compId,
    } = await syncSKV(creationDate.getTime(), order.items, amount, receiptCounter);

    if (success) {
      updateStorage({
        taxComplianceControlUnitSerial: controlUnitSerial,
        taxComplianceProductionNumber: productionNumber,
      });

      setComplianceId(compId);

      if (isStorageOrder) {
        // Set compliance ID in the storage order so we sync to SKV again, causing diffs
        updateStorage({
          storedOrders: storageRef.current.storedOrders.map((o) => {
            if (o.order.id === orderId) {
              return {
                ...o,
                complianceId: compId,
              };
            }
            return o;
          }),
        });
      }

      logger.info(`Successful SKV sync for order ${order.id}`, { orderId: order.id });
    } else {
      // If sync fails, wait 3 seconds and try again until max count. Then proceed without complianceId
      if (failCount < MAX_FAIL_COUNT) {
        failCount = failCount + 1;

        logger.warn(`Failed SKV sync for order ${order.id}`, {
          orderId: order.id,
          errorMessage,
        });

        setTimeout(() => {
          run();
        }, 3000);
      } else {
        dispatch(
          actions.addError({
            orderId,
            errorType: 'SKV',
            title: i18n('SKV.UnableContactSrv4pos'),
            message:
              typeof errorMessage === 'string'
                ? errorMessage
                : i18n('SKV.UnableContactSrv4pos.Description'),
          }),
        );

        logger.error(`Aborted SKV sync for order ${order.id}`, {
          orderId: order.id,
          errorMessage,
        });

        setProceedWithoutComplianceId(true);
      }
    }
  };

  useEffect(() => {
    logger.info(`Initiating SKV sync for order: ${order.id} | Amount: ${amount}`, {
      orderId: order.id,
    });

    if (complianceId) {
      return;
    }

    const isZeroAmountOrder = orderData?.amount === 0;

    /* Skip SKV sync if order has 0 amount since it will never result in success being true */
    if (isZeroAmountOrder) {
      setProceedWithoutComplianceId(true);
    } else {
      run();
    }

    // 2021-12-20: Skipping SKV sync for invoice payments is paused for now /JB
    // Skip SKV sync if invoice payment
    // if (!invoicePayment) {
    //   run();
    // } else {
    //   setProceedWithoutComplianceId(true);
    // }
  }, []);

  if (complianceId || proceedWithoutComplianceId) {
    return (
      <OrderSyncer
        orderId={orderId}
        complianceId={complianceId}
        creationDate={creationDate}
        isStorageOrder={isStorageOrder}
      />
    );
  }

  return null;
};

export default SKV;
